import {
  EMPTY_LIST,
  EMPTY_OBJECT,
  NOTIFICATION_TYPE,
} from '../../../constants/common'
import {
  filterPermittedActionsWithDividers,
  parseActionsOnValidDivider,
} from '../../../utils/massActions'
import { isDefaultOldApiAvatar } from '../../../utils/user'
import { getTableVisibleColumns } from '../../Table'
import {
  isTaskDone,
  isTaskDoneCommitType,
  TASK_ACTION_ATTACH_EMAILS,
  TASK_ACTION_ATTACH_FILE,
  TASK_ACTION_MARK_AS_DONE,
  TASK_ACTION_MARK_AS_HIDDEN,
  TASK_ACTION_MARK_AS_OPEN,
  TASK_ACTION_MARK_AS_SHARED,
  TASK_ACTION_SEND_TO_DWELLER,
  TASK_ACTION_SEND_TO_PROVIDER,
  TaskTableCellName,
} from '../../Task'

import {
  ACTIVITIES_DND_ID_PREFIX,
  ACTIVITIES_DND_TYPE,
  ACTIVITIES_FIXED_WIDTH_COLUMNS,
  ACTIVITIES_TABLE_CELL_NAME_ACTIONS,
  ACTIVITY_ACTION_ATTACH_FROM_DOCUMENT_STORAGE,
  ACTIVITY_ACTION_ATTACH_FROM_REQUEST_FOLDER,
  ACTIVITY_ACTION_EDIT,
  ACTIVITY_ITEM_ACTIONS,
  ACTIVITY_NOTIFICATION_TITLE_KEY,
  ACTIVITY_SEND_EMAIL_ACTIONS,
  PHASE_ACTION_ADD_ACTIVITY,
  PHASE_ACTION_TOGGLE_VISIBILITY,
  PHASE_COMPLETED_PROGRESS,
  REQUEST_PROFILE_TAB_ACTIVITIES_PHASES_MAX_COUNT,
  REQUEST_PROFILE_TAB_ACTIVITIES_TABLE_COLUMNS_CONFIG,
} from './RequestProfileTabActivities.constants'

function isActivitiesColumnVisible(columnName, permissions) {
  if (columnName === ACTIVITIES_TABLE_CELL_NAME_ACTIONS) {
    return permissions?.can_edit || permissions?.can_send_email_message
  }

  if (columnName === TaskTableCellName.VisibleToTenant) {
    return getCanViewActivitiesVisibleToDwellers(permissions)
  }

  return true
}

function normalizeActivityUser(user) {
  return !user
    ? null
    : {
        ...user,
        avatar: isDefaultOldApiAvatar(user.avatar) ? null : user.avatar,
        fullname: [user.name, user.second_name].filter(Boolean).join(' '),
      }
}

export function isPhaseActionToggleVisibility(action) {
  return action === PHASE_ACTION_TOGGLE_VISIBILITY
}

export function getCanViewActivitiesVisibleToDwellers(permissions) {
  return Boolean(permissions?.can_view_visible_to_dwellers)
}

export function getRequestProfileTabActivitiesTableColumns(permissions) {
  return getTableVisibleColumns(
    REQUEST_PROFILE_TAB_ACTIVITIES_TABLE_COLUMNS_CONFIG
  )
    .filter(column => isActivitiesColumnVisible(column.name, permissions))
    .map(column =>
      ACTIVITIES_FIXED_WIDTH_COLUMNS.has(column.name)
        ? column
        : { ...column, width: undefined }
    )
}

export function getActivityItemActions(permissions) {
  return ACTIVITY_ITEM_ACTIONS.filter(action =>
    ACTIVITY_SEND_EMAIL_ACTIONS.has(action.value)
      ? permissions?.can_send_email_message
      : permissions?.can_edit
  )
}

export function getPhaseDndId(phaseId) {
  return `${ACTIVITIES_DND_ID_PREFIX.phase}-${phaseId}`
}

export function getActivityDndId(activityId) {
  return `${ACTIVITIES_DND_ID_PREFIX.activity}-${activityId}`
}

export function findDndIndex({ getDndId, items, targetDndId }) {
  return items.findIndex(item => getDndId(item.id) === targetDndId)
}

export function getDndDropIndicator({ index, newIndex }) {
  return newIndex > index ? 'above' : 'below'
}

// Activities can only be reordered inside their own phase, so rows of other
// phases never show the drop indicator.
export function isDndDropTarget({
  active,
  data,
  isDragging,
  isOver,
  isSorting,
  type,
}) {
  const activeData = active?.data?.current

  return (
    isSorting &&
    isOver &&
    !isDragging &&
    activeData?.type === type &&
    activeData?.phaseId === data?.phaseId
  )
}

export function getActivityDropPosition({ activities, newIndex }) {
  return activities[newIndex]?.position ?? newIndex
}

export function isActivitiesPhaseType(type) {
  return type === ACTIVITIES_DND_TYPE.phase
}

export function isActivitiesActivityType(type) {
  return type === ACTIVITIES_DND_TYPE.activity
}

export function isPhaseCompleted(phase) {
  return phase?.progress === PHASE_COMPLETED_PROGRESS
}

export function isRequestProfileTabActivitiesPhasePopulatable(phase) {
  return Boolean(phase?.is_populatable) && !phase?.is_manual
}

// Connected (iDWELL-to-iDWELL) requests have no header pipeline of their own,
// so a phase can only be added to a request the user manages directly.
export function getCanAddRequestProfileTabActivitiesPhase({
  outboundUuid,
  permissions,
}) {
  return Boolean(permissions?.can_add_activity) && !outboundUuid
}

export function isRequestProfileTabActivitiesPhasesLimitReached(phases) {
  return (
    (phases?.length ?? 0) >= REQUEST_PROFILE_TAB_ACTIVITIES_PHASES_MAX_COUNT
  )
}

// Backend phase positions are 1-based (ID-139): the divider above the phase at
// 0-based `index` inserts into that phase's slot, which is `index + 1`.
export function getRequestProfileTabActivitiesInsertPhasePosition(index) {
  return index + 1
}

export function hasRequestProfileTabActivitiesPhaseAbove(index) {
  return index > 0
}

export function getRequestProfileTabActivitiesPhasesForUser({
  canViewAll,
  phases = EMPTY_LIST,
}) {
  if (canViewAll) {
    return phases
  }

  return phases
    .map(phase => ({
      ...phase,
      activities:
        phase.activities?.filter(activity => activity.visible_to_tenant) ||
        EMPTY_LIST,
    }))
    .filter(phase => phase.activities.length > 0)
}

export function getRequestProfileTabActivitiesPhaseCounter(activities) {
  const total = activities?.length || 0
  const done = activities?.filter(activity => isTaskDone(activity)).length || 0

  return { done, total }
}

export function getRequestProfileTabActivitiesTableItem(activity) {
  return {
    ...activity,
    activity_type: activity.type,
    counters: {
      active_automations: activity.active_automation_count,
      files: activity.file_count,
      threads: activity.request_thread_count,
      unread_threads: activity.unread_request_thread_count,
    },
    manager: normalizeActivityUser(activity.manager),
  }
}

export function getRequestProfileTabActivitiesTableItems(activities) {
  return activities?.map(getRequestProfileTabActivitiesTableItem) || EMPTY_LIST
}

export function getRequestProfileTabActivitiesItemActions(
  item,
  canEditActivities
) {
  const isDone = isTaskDone(item)

  const isDoneType = isTaskDoneCommitType(item)

  const items = [
    {
      label: 'RequestProfileTabActivities.EditActivityActionKey',
      permission: true,
      value: ACTIVITY_ACTION_EDIT,
    },
    {
      label: 'RequestProfileTabActivities.ShowForDwellersActionKey',
      permission: canEditActivities && !item.visible_to_tenant,
      value: TASK_ACTION_MARK_AS_SHARED,
    },
    {
      label: 'RequestProfileTabActivities.HideForDwellersActionKey',
      permission: canEditActivities && item.visible_to_tenant,
      value: TASK_ACTION_MARK_AS_HIDDEN,
    },
    {
      label: 'Tasks.MarkAsDoneActionOptionKey',
      permission: isDoneType && !isDone,
      value: TASK_ACTION_MARK_AS_DONE,
    },
    {
      label: 'Tasks.MarkAsOpenActionOptionKey',
      permission: isDoneType && isDone,
      value: TASK_ACTION_MARK_AS_OPEN,
    },
    {
      label: 'RequestProfileTabActivities.SendEmailToProviderActionKey',
      permission: true,
      value: TASK_ACTION_SEND_TO_PROVIDER,
    },
    {
      label: 'RequestProfileTabActivities.SendEmailToApplicantActionKey',
      permission: true,
      value: TASK_ACTION_SEND_TO_DWELLER,
    },
    {
      label: 'RequestProfileTabActivities.AttachEmailActionKey',
      permission: true,
      value: TASK_ACTION_ATTACH_EMAILS,
    },
    {
      label: 'RequestProfileTabActivities.AttachFromDesktopActionKey',
      permission: true,
      value: TASK_ACTION_ATTACH_FILE,
    },
    {
      label: 'RequestProfileTabActivities.AttachFromDocumentStorageActionKey',
      permission: true,
      value: ACTIVITY_ACTION_ATTACH_FROM_DOCUMENT_STORAGE,
    },
    {
      label: 'RequestProfileTabActivities.AttachFromRequestFolderActionKey',
      permission: true,
      value: ACTIVITY_ACTION_ATTACH_FROM_REQUEST_FOLDER,
    },
  ]

  return parseActionsOnValidDivider(filterPermittedActionsWithDividers(items))
}

export function getPhaseFilesApiProps({ ordering, page, parent }) {
  return { ordering, page, parent }
}

export function getPhaseFilesFromResponse(data) {
  return { count: data?.length ?? 0, items: data ?? EMPTY_LIST, parent: null }
}

export function getPhaseFilesPaginationData(data) {
  const count = data?.length ?? 0

  return { count, pageCount: 1, pageSize: count }
}

export function getActivityCreationSuccessNotificationProps({
  request,
  t,
  title,
}) {
  return {
    text: t('Notification.TaskCreatedText', {
      request_number: request.request_no,
      request_title: request.title,
      title,
    }),
    title: t('Notification.TaskCreatedTitle'),
    type: NOTIFICATION_TYPE.success,
  }
}

export function getActivityEditSuccessNotificationProps(t) {
  return {
    title: t('Notification.CommonTitleSavedKey'),
    type: NOTIFICATION_TYPE.success,
  }
}

export function getActivityStatusChangeNotificationKey(
  data,
  { isError } = EMPTY_OBJECT
) {
  const action = data.done || data.yesno != null ? 'done' : 'open'

  return ACTIVITY_NOTIFICATION_TITLE_KEY[isError ? `${action}Error` : action]
}

export function getActivityVisibilityChangeNotificationKey(
  isVisible,
  { isError } = EMPTY_OBJECT
) {
  const action = isVisible ? 'shared' : 'hidden'

  return ACTIVITY_NOTIFICATION_TITLE_KEY[isError ? `${action}Error` : action]
}

export function getRequestProfileTabActivitiesPhaseActions({
  canToggleVisibility,
  isVisible,
  t,
}) {
  const items = [
    {
      label: t('RequestProfileTabActivities.AddActivityActionKey'),
      permission: true,
      value: PHASE_ACTION_ADD_ACTIVITY,
    },
    {
      label: t(
        isVisible
          ? 'RequestProfileTabActivities.HideForDwellersActionKey'
          : 'RequestProfileTabActivities.ShowForDwellersActionKey'
      ),
      permission: canToggleVisibility,
      value: PHASE_ACTION_TOGGLE_VISIBILITY,
    },
  ]

  return parseActionsOnValidDivider(filterPermittedActionsWithDividers(items))
}

export function getActivitiesById(activities) {
  return (
    activities?.reduce((acc, activity) => {
      acc[activity.id] = activity

      return acc
    }, {}) || EMPTY_OBJECT
  )
}
